import JSZip from 'jszip';
import { DownloadError } from './downloadError';
import { DOWNLOAD_MESSAGES } from './downloadMessages';
import {
  buildStudyArchiveFilename,
  buildStudyJpegFilename,
  getStudyFilenameParts,
} from './filenameUtils';
import { JPEG_YIELD_EVERY_N_IMAGES, yieldToMainThread } from './jpegConversion';
import { renderJpegBlobFromImageId } from './renderJpegFromImageId';
import { collectStudyDicomInstances } from './resolveViewportStudy';
import type { DicomZipProgressCallback, StudyImageExportTarget } from './types';

export async function buildStudyJpegZip(
  studyInstanceUID: string,
  targets: StudyImageExportTarget[],
  extensionManager: AppTypes.ExtensionManager,
  onProgress?: DicomZipProgressCallback,
  servicesManager?: AppTypes.ServicesManager
): Promise<{ blob: Blob; filename: string; includedCount: number }> {
  const instances = collectStudyDicomInstances(studyInstanceUID);
  const { studyDate, studyId } = getStudyFilenameParts(
    studyInstanceUID,
    instances,
    servicesManager
  );
  const zip = new JSZip();
  let includedCount = 0;

  for (let index = 0; index < targets.length; index += 1) {
    const target = targets[index];

    try {
      const jpegBlob = await renderJpegBlobFromImageId(target.imageId, extensionManager);
      const entryName = buildStudyJpegFilename(studyDate, studyId, target.exportIndex);
      zip.file(entryName, jpegBlob, { compression: 'STORE' });
      includedCount += 1;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ViewportDownload] Skipping JPEG export target:', target, error);
      }
    }

    onProgress?.(index + 1, targets.length);

    if ((index + 1) % JPEG_YIELD_EVERY_N_IMAGES === 0) {
      await yieldToMainThread();
    }
  }

  if (!includedCount) {
    return {
      blob: new Blob(),
      filename: buildStudyArchiveFilename(studyDate, studyId),
      includedCount: 0,
    };
  }

  await yieldToMainThread();

  try {
    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'STORE',
    });

    if (!blob || blob.size === 0) {
      throw new DownloadError(DOWNLOAD_MESSAGES.ZIP_GENERATION_FAILED);
    }

    return {
      blob,
      filename: buildStudyArchiveFilename(studyDate, studyId),
      includedCount,
    };
  } catch (error) {
    if (error instanceof DownloadError) {
      throw error;
    }

    throw new DownloadError(DOWNLOAD_MESSAGES.ZIP_GENERATION_FAILED, error);
  }
}
